/**
 * Enhanced AI API Service
 * Enterprise-grade service layer for Spring AI backend integration
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
  AIApiResponse,
  AIError,
  AIErrorCode,
  AIMessage,
  AIConversation,
  StructuredContentRequest,
  StructuredContentResponse,
  ContentAnalysisRequest,
  ContentAnalysisResponse,
  AIModelType,
  SupportedLanguage,
  TTSRequest,
  TTSResponse,
  TTSVoice,
  TTSHealthStatus,
  TTSVoiceInfo,
  TTSLanguageInfo,
  JournalistArticleRequest,
  JournalistArticleResponse
} from '../types/aiTypes';
import { AI_CONFIG, PERFORMANCE_CONFIG, SECURITY_CONFIG } from '../config/aiConfig';

// ================== UNICODE-SAFE BASE64 ENCODING ==================

/**
 * Encodes a Unicode string to base64 safely.
 * Handles characters outside the Latin1 range (e.g., Telugu, Hindi, etc.)
 * 
 * @param str The string to encode (can contain Unicode characters)
 * @returns Base64 encoded string safe for use as cache keys
 */
const safeBase64Encode = (str: string): string => {
  try {
    // Use TextEncoder to convert string to UTF-8 bytes
    const utf8Bytes = new TextEncoder().encode(str);
    // Convert bytes to binary string
    const binaryString = Array.from(utf8Bytes, byte => String.fromCharCode(byte)).join('');
    // Base64 encode the binary string
    return btoa(binaryString);
  } catch (error) {
    // Fallback: use encodeURIComponent for cache key if encoding fails
    console.warn('Base64 encoding failed, using URI encoding fallback:', error);
    return encodeURIComponent(str);
  }
};

// ================== RATE LIMITING ==================

class RateLimiter {
  private requests: number[] = [];
  
  canMakeRequest(): boolean {
    const now = Date.now();
    const minute = 60 * 1000;
    
    // Remove requests older than 1 minute
    this.requests = this.requests.filter(time => now - time < minute);
    
    return this.requests.length < PERFORMANCE_CONFIG.RATE_LIMITING.REQUESTS_PER_MINUTE;
  }
  
  recordRequest(): void {
    this.requests.push(Date.now());
  }
  
  getTimeUntilReset(): number {
    if (this.requests.length === 0) return 0;
    const oldest = Math.min(...this.requests);
    return Math.max(0, (oldest + 60 * 1000) - Date.now());
  }
}

// ================== CACHE MANAGER ==================

class CacheManager {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  
  set(key: string, data: any, ttl: number): void {
    // Implement LRU eviction if cache is full
    if (this.cache.size >= PERFORMANCE_CONFIG.CACHE.MAX_CACHE_SIZE) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }
  
  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }
  
  clear(): void {
    this.cache.clear();
  }
  
  has(key: string): boolean {
    return this.get(key) !== null;
  }
}

// ================== RETRY MANAGER ==================

class RetryManager {
  static async withRetry<T>(
    operation: () => Promise<T>,
    maxAttempts: number = PERFORMANCE_CONFIG.RETRY.MAX_ATTEMPTS
  ): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        // Don't retry for certain error types
        if (error instanceof AIError && !error.retryable) {
          throw error;
        }
        
        if (attempt < maxAttempts) {
          const delay = Math.min(
            PERFORMANCE_CONFIG.RETRY.INITIAL_DELAY * Math.pow(PERFORMANCE_CONFIG.RETRY.BACKOFF_MULTIPLIER, attempt - 1),
            PERFORMANCE_CONFIG.RETRY.MAX_DELAY
          );
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError!;
  }
}

// ================== AI API SERVICE ==================

export class AIApiService {
  private api: AxiosInstance;
  private rateLimiter = new RateLimiter();
  private cache = new CacheManager();
  
  constructor() {
    this.api = axios.create({
      baseURL: AI_CONFIG.baseURL,
      timeout: AI_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
        ...(AI_CONFIG.apiKey && { 'Authorization': `Bearer ${AI_CONFIG.apiKey}` })
      },
      withCredentials: SECURITY_CONFIG.API_SECURITY.USE_HTTPS
    });
    
    this.setupInterceptors();
  }
  
  private setupInterceptors(): void {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Rate limiting check
        if (!this.rateLimiter.canMakeRequest()) {
          const resetTime = this.rateLimiter.getTimeUntilReset();
          throw new AIError(
            `Rate limit exceeded. Try again in ${Math.ceil(resetTime / 1000)} seconds`,
            AIErrorCode.RATE_LIMIT_EXCEEDED,
            true,
            { resetTime }
          );
        }
        
        this.rateLimiter.recordRequest();
        
        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();

        return config;
      },
      (error) => Promise.reject(this.handleError(error))
    );
    
    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => Promise.reject(this.handleError(error))
    );
  }
  
  private generateRequestId(): string {
    return `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private handleError(error: AxiosError): AIError {
    if (!error.response) {
      return new AIError(
        'Network error or service unavailable',
        AIErrorCode.NETWORK_ERROR,
        true,
        { originalError: error.message }
      );
    }
    
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return new AIError(
          'Invalid request parameters',
          AIErrorCode.INVALID_INPUT,
          false,
          data
        );
      case 401:
        return new AIError(
          'Authentication failed',
          AIErrorCode.AUTHENTICATION_FAILED,
          false,
          data
        );
      case 429:
        return new AIError(
          'Rate limit exceeded',
          AIErrorCode.RATE_LIMIT_EXCEEDED,
          true,
          data
        );
      case 503:
        return new AIError(
          'AI model temporarily unavailable',
          AIErrorCode.MODEL_UNAVAILABLE,
          true,
          data
        );
      default:
        return new AIError(
          'Unknown API error',
          AIErrorCode.UNKNOWN_ERROR,
          status >= 500,
          data
        );
    }
  }
  
  private async makeRequest<T>(
    method: 'GET' | 'POST' | 'DELETE',
    endpoint: string,
    data?: any,
    options?: {
      timeout?: number;
      cacheKey?: string;
      cacheTtl?: number;
    }
  ): Promise<T> {
    // Check cache first
    if (options?.cacheKey && method === 'GET') {
      const cached = this.cache.get(options.cacheKey);
      if (cached) return cached;
    }
    
    const config = {
      method,
      url: endpoint,
      ...(data && (method === 'POST' ? { data } : { params: data })),
      ...(options?.timeout && { timeout: options.timeout })
    };
    
    const response = await RetryManager.withRetry(async () => {
      return this.api.request<AIApiResponse<T>>(config);
    });
    
    const responseData = response.data;
    const result = responseData.data !== undefined ? responseData.data : responseData as T;
    
    // Cache successful responses
    if (options?.cacheKey && options?.cacheTtl && method === 'GET') {
      this.cache.set(options.cacheKey, result, options.cacheTtl);
    }
    
    return result;
  }
  
  // ================== CHATBOT METHODS ==================
  
  async sendChatMessage(
    conversationId: string,
    message: string,
    topic?: string,
    language: SupportedLanguage = 'English'
  ): Promise<string> {
    const params = new URLSearchParams({
      conversationId,
      message,
      ...(topic && { topic })
    });
    
    return this.makeRequest<string>(
      'POST',
      `${AI_CONFIG.endpoints.chatContextual}?${params}`,
      undefined,
      { timeout: PERFORMANCE_CONFIG.TIMEOUTS.CHAT_MESSAGE }
    );
  }
  
  async sendSimpleChat(
    message: string,
    language: SupportedLanguage = 'English'
  ): Promise<string> {
    const params = new URLSearchParams({ message, language });
    
    return this.makeRequest<string>(
      'POST',
      `${AI_CONFIG.endpoints.chatSimple}?${params}`,
      undefined,
      { timeout: PERFORMANCE_CONFIG.TIMEOUTS.CHAT_MESSAGE }
    );
  }
  
  async clearConversation(conversationId: string): Promise<void> {
    await this.makeRequest<void>(
      'DELETE',
      `${AI_CONFIG.endpoints.clearChat}/${conversationId}`
    );
    
    // Clear related cache entries
    this.cache.clear();
  }
  
  async getActiveConversations(): Promise<string[]> {
    return this.makeRequest<string[]>(
      'GET',
      AI_CONFIG.endpoints.activeConversations,
      undefined,
      {
        cacheKey: 'active_conversations',
        cacheTtl: PERFORMANCE_CONFIG.CACHE.CONVERSATIONS_TTL
      }
    );
  }
  
  // ================== STRUCTURED CONTENT METHODS ==================
  
  async generateStructuredContent(
    request: StructuredContentRequest
  ): Promise<StructuredContentResponse> {
    const params = new URLSearchParams({
      language: request.language,
      contentType: request.contentType || 'news article',
      audience: request.audience || 'general readers'
    });
    
    const cacheKey = `structured_${safeBase64Encode(JSON.stringify(request))}`;
    
    return this.makeRequest<StructuredContentResponse>(
      'POST',
      `${AI_CONFIG.endpoints.structuredContent}?${params}`,
      request.inputContent,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_GENERATION,
        cacheKey,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.STRUCTURED_CONTENT_TTL
      }
    );
  }
  
  async generateQuickStructuredContent(
    inputContent: string,
    language: SupportedLanguage
  ): Promise<StructuredContentResponse> {
    const params = new URLSearchParams({ language });
    
    const cacheKey = `quick_structured_${safeBase64Encode(inputContent + language)}`;
    
    return this.makeRequest<StructuredContentResponse>(
      'POST',
      `${AI_CONFIG.endpoints.quickStructuredContent}?${params}`,
      inputContent,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_GENERATION,
        cacheKey,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.STRUCTURED_CONTENT_TTL
      }
    );
  }
  
  // ================== CONTENT ANALYSIS METHODS ==================
  
  async summarizeContent(
    content: string,
    language: SupportedLanguage = 'English',
    maxLength?: number
  ): Promise<ContentAnalysisResponse> {
    const request: ContentAnalysisRequest = {
      content,
      analysisType: 'summarize',
      language,
      options: { maxLength }
    };
    
    const cacheKey = `summary_${safeBase64Encode(content + language)}`;
    
    return this.makeRequest<ContentAnalysisResponse>(
      'POST',
      AI_CONFIG.endpoints.summarize,
      request,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_ANALYSIS,
        cacheKey,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.ANALYSIS_RESULTS_TTL
      }
    );
  }
  
  async factCheckContent(
    content: string,
    language: SupportedLanguage = 'English'
  ): Promise<ContentAnalysisResponse> {
    const request: ContentAnalysisRequest = {
      content,
      analysisType: 'fact-check',
      language,
      options: { includeSources: true }
    };
    
    const cacheKey = `factcheck_${safeBase64Encode(content + language)}`;
    
    return this.makeRequest<ContentAnalysisResponse>(
      'POST',
      AI_CONFIG.endpoints.factCheck,
      request,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_ANALYSIS,
        cacheKey,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.ANALYSIS_RESULTS_TTL
      }
    );
  }
  
  async generateHeadlines(
    content: string,
    language: SupportedLanguage = 'English',
    count: number = 5
  ): Promise<string[]> {
    const request = {
      content,
      language,
      count
    };
    
    const cacheKey = `headlines_${safeBase64Encode(content + language + count)}`;
    
    return this.makeRequest<string[]>(
      'POST',
      AI_CONFIG.endpoints.generateHeadlines,
      request,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_ANALYSIS,
        cacheKey,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.ANALYSIS_RESULTS_TTL
      }
    );
  }
  
  async generateArticle(
    topic: string,
    language: SupportedLanguage = 'English',
    length: 'short' | 'medium' | 'long' = 'medium'
  ): Promise<StructuredContentResponse> {
    const request = {
      topic,
      language,
      length
    };
    
    const cacheKey = `article_${safeBase64Encode(topic + language + length)}`;
    
    return this.makeRequest<StructuredContentResponse>(
      'POST',
      AI_CONFIG.endpoints.generateArticle,
      request,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_GENERATION,
        cacheKey,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.STRUCTURED_CONTENT_TTL
      }
    );
  }
  
  // ================== TEXT-TO-SPEECH (TTS) METHODS ==================
  
  /**
   * Generate speech audio from text with full configuration options.
   * @param request TTS request with text, voice, and configuration
   * @returns TTSResponse with audio URL and metadata
   */
  async generateSpeech(request: TTSRequest): Promise<TTSResponse> {
    return this.makeRequest<TTSResponse>(
      'POST',
      AI_CONFIG.endpoints.ttsGenerate,
      request,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.TTS_GENERATION || 60000 // 60 seconds for audio generation
      }
    );
  }
  
  /**
   * Generate news-optimized speech audio.
   * Uses HD quality model with professional voice settings.
   * @param text Text content to convert to speech
   * @param language Target language (e.g., 'en', 'te' for Telugu)
   * @returns TTSResponse with audio URL
   */
  async generateNewsAudio(
    text: string,
    language: string = 'en'
  ): Promise<TTSResponse> {
    const params = new URLSearchParams({ text, language });
    
    return this.makeRequest<TTSResponse>(
      'POST',
      `${AI_CONFIG.endpoints.ttsGenerateNews}?${params}`,
      undefined,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.TTS_GENERATION || 60000
      }
    );
  }
  
  /**
   * Generate speech audio for article content.
   * Associates generated audio with the article ID for mapping.
   * @param text Article body text to convert
   * @param language Target language
   * @param articleId Article ID for association
   * @param articleTitle Article title for file naming
   * @returns TTSResponse with audio URL
   */
  async generateArticleAudio(
    text: string,
    language: string = 'en',
    articleId?: string,
    articleTitle?: string
  ): Promise<TTSResponse> {
    const params = new URLSearchParams({ 
      text, 
      language,
      ...(articleId && { articleId }),
      ...(articleTitle && { articleTitle })
    });
    
    return this.makeRequest<TTSResponse>(
      'POST',
      `${AI_CONFIG.endpoints.ttsGenerateArticle}?${params}`,
      undefined,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.TTS_GENERATION || 60000
      }
    );
  }
  
  /**
   * Get available TTS voices with descriptions.
   * @returns TTSVoiceInfo with available voices and recommendations
   */
  async getTTSVoices(): Promise<TTSVoiceInfo> {
    return this.makeRequest<TTSVoiceInfo>(
      'GET',
      AI_CONFIG.endpoints.ttsVoices,
      undefined,
      {
        cacheKey: 'tts_voices',
        cacheTtl: PERFORMANCE_CONFIG.CACHE.CONVERSATIONS_TTL // Cache for 24 hours
      }
    );
  }
  
  /**
   * Get supported TTS languages.
   * @returns TTSLanguageInfo with supported languages and details
   */
  async getTTSLanguages(): Promise<TTSLanguageInfo> {
    return this.makeRequest<TTSLanguageInfo>(
      'GET',
      AI_CONFIG.endpoints.ttsLanguages,
      undefined,
      {
        cacheKey: 'tts_languages',
        cacheTtl: PERFORMANCE_CONFIG.CACHE.CONVERSATIONS_TTL
      }
    );
  }
  
  /**
   * Get recommended voice for a specific language.
   * @param language Language code or name (e.g., 'te', 'Telugu')
   * @returns Recommended voice for the language
   */
  async getRecommendedVoice(
    language: string
  ): Promise<{ language: string; recommendedVoice: TTSVoice; reason: string }> {
    const params = new URLSearchParams({ language });
    
    return this.makeRequest<{ language: string; recommendedVoice: TTSVoice; reason: string }>(
      'GET',
      `${AI_CONFIG.endpoints.ttsVoiceRecommend}?${params}`,
      undefined,
      {
        cacheKey: `tts_voice_recommend_${language}`,
        cacheTtl: PERFORMANCE_CONFIG.CACHE.CONVERSATIONS_TTL
      }
    );
  }
  
  /**
   * Check TTS service health status.
   * @returns TTSHealthStatus with service availability
   */
  async getTTSHealth(): Promise<TTSHealthStatus> {
    return this.makeRequest<TTSHealthStatus>(
      'GET',
      AI_CONFIG.endpoints.ttsHealth,
      undefined,
      {
        timeout: PERFORMANCE_CONFIG.TIMEOUTS.HEALTH_CHECK
      }
    );
  }
  
  // ================== HEALTH CHECK ==================
  
  async healthCheck(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    timestamp: Date;
    services: Record<string, boolean>;
  }> {
    try {
      const response = await this.makeRequest<any>(
        'GET',
        AI_CONFIG.endpoints.health,
        undefined,
        { timeout: PERFORMANCE_CONFIG.TIMEOUTS.HEALTH_CHECK }
      );
      
      return {
        status: 'healthy',
        timestamp: new Date(),
        services: response || {}
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        timestamp: new Date(),
        services: {}
      };
    }
  }
  
  // ================== UTILITY METHODS ==================
  
  clearCache(): void {
    this.cache.clear();
  }
  
  getCacheStats(): {
    size: number;
    maxSize: number;
    hitRate: number;
  } {
    return {
      size: this.cache['cache'].size,
      maxSize: PERFORMANCE_CONFIG.CACHE.MAX_CACHE_SIZE,
      hitRate: 0 // TODO: Implement hit rate tracking
    };
  }
  
  getRateLimitStatus(): {
    requestsInLastMinute: number;
    maxRequestsPerMinute: number;
    timeUntilReset: number;
  } {
    return {
      requestsInLastMinute: this.rateLimiter['requests'].length,
      maxRequestsPerMinute: PERFORMANCE_CONFIG.RATE_LIMITING.REQUESTS_PER_MINUTE,
      timeUntilReset: this.rateLimiter.getTimeUntilReset()
    };
  }

  // ================== JOURNALIST CONTENT GENERATION ==================

  async generateJournalistArticle(
    request: JournalistArticleRequest
  ): Promise<JournalistArticleResponse> {
    return this.makeRequest<JournalistArticleResponse>(
      'POST',
      '/journalist/generate',
      request,
      { timeout: PERFORMANCE_CONFIG.TIMEOUTS.CONTENT_GENERATION }
    );
  }
}

// ================== SINGLETON INSTANCE ==================

const aiApiService = new AIApiService();
export default aiApiService;
